import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { IconCamera, IconX, IconKeyboard } from '@tabler/icons-react';
import { toast } from 'react-toastify';

export default function QRScanner({ onScanSuccess, onClose, isOpen }) {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);

  useEffect(() => {
    if (isOpen && !showManualInput) {
      startScanner();
    } else {
      stopScanner();
    }

    return () => {
      stopScanner();
    };
  }, [isOpen, showManualInput]);

  const startScanner = async () => {
    try {
      html5QrCodeRef.current = new Html5Qrcode('qr-reader');
      const config = { fps: 10, qrbox: { width: 250, height: 250 } };
      
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        config,
        (decodedText) => {
          handleScanSuccess(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors (normal during scanning)
        }
      );
      
      setIsScanning(true);
      setError('');
    } catch (err) {
      console.error('Scanner error:', err);
      setError('Gagal mengakses kamera. Pastikan izin kamera diberikan.');
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        setIsScanning(false);
      } catch (err) {
        console.error('Stop scanner error:', err);
      }
    }
  };

  const handleScanSuccess = (decodedText) => {
    // Play sound effect
    const audio = new Audio('/scan-beep.mp3');
    audio.play().catch(() => {}); // Ignore audio errors
    
    onScanSuccess(decodedText);
    
    // Pause scanning briefly to prevent multiple scans
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.pause();
      setTimeout(() => {
        html5QrCodeRef.current.resume();
      }, 1500);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      onScanSuccess(manualInput.trim());
      setManualInput('');
    }
  };

  const toggleManualInput = () => {
    setShowManualInput(!showManualInput);
    setError('');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
            Scan QR Code
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors"
          >
            <IconX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {!showManualInput ? (
            <>
              {/* Scanner Container */}
              <div className="relative">
                <div
                  id="qr-reader"
                  ref={scannerRef}
                  className="w-full aspect-square bg-slate-100 dark:bg-slate-800 rounded-xl overflow-hidden"
                ></div>
                
                {/* Scanner Overlay */}
                {isScanning && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-48 h-48 border-2 border-amber-500 rounded-lg animate-pulse"></div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Manual Input Toggle */}
              <button
                onClick={toggleManualInput}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium transition-colors"
              >
                <IconKeyboard className="w-5 h-5" />
                <span>Input Manual</span>
              </button>
            </>
          ) : (
            <>
              {/* Manual Input Form */}
              <form onSubmit={handleManualSubmit}>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                  Masukkan Kode Barang
                </label>
                <input
                  type="text"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="Scan atau ketik kode barang..."
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 transition-all outline-none"
                  autoFocus
                />
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={toggleManualInput}
                    className="flex-1 px-4 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={!manualInput.trim()}
                    className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 dark:disabled:text-slate-400 rounded-lg text-white font-bold transition-colors"
                  >
                    Proses
                  </button>
                </div>
              </form>

              {/* Back to Scanner */}
              <button
                onClick={toggleManualInput}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium transition-colors"
              >
                <IconCamera className="w-5 h-5" />
                <span>Kembali ke Scanner</span>
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
            Arahkan kamera ke QR code pada bandrol harga barang
          </p>
        </div>
      </div>
    </div>
  );
}
